// "What stands out" for the gates page: plain-language findings about when and where people came
// in and left, ranked so the most useful ones come first. Same card format as the concessions insights.
import React from 'react';
import { intervalFlow, minuteToClock, clockToMinute, combinedCountSeries, peakQueue, isLongQueue, QUEUE_TARGET } from './gatesMetrics';
import { formatDuration } from '../Concession/live/liveMetrics';

// Rushes are measured over this many minutes
const RUSH_MINUTES = 15;
// No exit wave until exits are at least this many, and this share of entries (early leavers aren't a wave)
const MIN_EXITS = 30;
const MIN_EXIT_SHARE = 0.05;

// Entries/exits per minute, limited to the window
const minuteFlow = (series, range) => {
  const flow = intervalFlow(series, 1);
  return range ? flow.filter((b) => b.start >= range.start && b.start < range.end) : flow;
};

// Busiest quarter-hour for a field, from per-minute flow. Fixed slots (6:00, 6:15, ...), the same
// slots as the heat grid and the 15-minute chart, so the times agree everywhere on the page.
export const peakStretch = (flow, field, len = RUSH_MINUTES) => {
  const slots = new Map();
  flow.forEach((b) => {
    const start = Math.floor(b.start / len) * len;
    slots.set(start, (slots.get(start) || 0) + b[field]);
  });
  let best = null;
  slots.forEach((total, start) => {
    if (!best || total > best.total) best = { start, end: start + len, total };
  });
  return best && best.total > 0 ? best : null;
};

const durationText = (m) => (m >= 60 ? formatDuration(m) : `${m} min`);
// "20–35 min", "1h 55m – 2h 10m"
const rangeText = (a, b) => (b < 60 ? `${a}–${b} min` : `${durationText(a)} – ${durationText(b)}`);

// A quarter-hour relative to game start, as a range: "35–50 min before game start".
// (Measuring from the slot's start alone made rushes look earlier than they were.)
const relativeToStart = (slot, gameStart) => {
  if (gameStart == null) return null;
  const from = slot.start - gameStart;
  const to = slot.end - gameStart;
  if (from < 0 && to > 0) return 'around game start';
  if (to <= 0) {
    const near = Math.abs(to);
    const far = Math.abs(from);
    return near === 0 ? `in the ${RUSH_MINUTES} minutes before game start` : `${rangeText(near, far)} before game start`;
  }
  return from === 0 ? `in the first ${RUSH_MINUTES} minutes after game start` : `${rangeText(from, to)} after game start`;
};

// "Home Plate", "Home Plate and Left Field", "A, B and C"
const listNames = (names) => (names.length <= 1 ? names[0] || '' : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

const span = (p) => `${minuteToClock(p.start)} – ${minuteToClock(p.end)}`;
const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

export const buildGatesInsights = ({ gateStats, range, currentGame, isLive }) => {
  if (!gateStats.length) return [];
  const gameStart = clockToMinute(currentGame?.gameStartTime);
  const startInRange = gameStart != null && (!range || (gameStart > range.start && gameStart < range.end));

  const combined = combinedCountSeries(gateStats.map((g) => g.counts));
  const allFlow = minuteFlow(combined, range);
  const totalIn = allFlow.reduce((sum, b) => sum + b.in, 0);
  const totalOut = allFlow.reduce((sum, b) => sum + b.out, 0);
  const perGate = gateStats.map((g) => {
    const flow = minuteFlow(g.counts, range);
    return {
      label: g.label,
      flow,
      in: flow.reduce((sum, b) => sum + b.in, 0),
      out: flow.reduce((sum, b) => sum + b.out, 0),
      rush: peakStretch(flow, 'in'),
      queue: range ? g.queue.filter((p) => p.minute >= range.start && p.minute <= range.end) : g.queue,
    };
  });
  const insights = [];

  // 1. Entry rush across all gates
  const rush = peakStretch(allFlow, 'in');
  if (rush) {
    const when = relativeToStart(rush, startInRange ? gameStart : null);
    insights.push({
      key: 'rush',
      tone: 'info',
      icon: 'graph-up-arrow',
      score: 100,
      label: 'Entry rush',
      text: (
        <>
          Entries peaked <strong>{span(rush)}</strong>: <strong>{rush.total.toLocaleString()} people</strong> through all gates
          {' '}({Math.round(rush.total / RUSH_MINUTES)} a minute){when ? `, ${when}` : ''}.
        </>
      ),
    });
  }

  // 2. Which gates got busy when (gates that peaked in the same quarter-hour are named together)
  const rushed = perGate.filter((g) => g.rush && g.in >= 20).sort((a, b) => a.rush.start - b.rush.start);
  if (rushed.length >= 2) {
    const firstStart = rushed[0].rush.start;
    const lastStart = rushed[rushed.length - 1].rush.start;
    const firstGroup = rushed.filter((g) => g.rush.start === firstStart).map((g) => g.label);
    const lastGroup = rushed.filter((g) => g.rush.start === lastStart).map((g) => g.label);
    const gap = lastStart - firstStart;
    const slot = (start) => `${minuteToClock(start)} – ${minuteToClock(start + RUSH_MINUTES)}`;
    insights.push({
      key: 'timing',
      tone: 'info',
      icon: 'clock-history',
      score: 90,
      label: 'Gate timing',
      text: gap > 0 ? (
        <>
          <strong>{listNames(firstGroup)}</strong> {firstGroup.length > 1 ? 'were' : 'was'} busiest first
          {' '}(<strong>{slot(firstStart)}</strong>); <strong>{listNames(lastGroup)}</strong> peaked last
          {' '}(<strong>{slot(lastStart)}</strong>), {durationText(gap)} later.
        </>
      ) : (
        <>
          Every gate was busiest in the same quarter-hour: <strong>{slot(firstStart)}</strong>.
        </>
      ),
    });
  }

  // 3. Arrivals before and after the game started (not until the game has started: before
  // that, "100% came before game start" says nothing)
  const lastReading = combined.length ? combined[combined.length - 1].minute : null;
  const started = !isLive || (lastReading != null && lastReading >= gameStart);
  if (startInRange && started && totalIn > 0) {
    const before = allFlow.filter((b) => b.start < gameStart).reduce((sum, b) => sum + b.in, 0);
    const lastHour = allFlow.filter((b) => b.start >= gameStart - 60 && b.start < gameStart).reduce((sum, b) => sum + b.in, 0);
    const after = totalIn - before;
    insights.push({
      key: 'arrivals',
      tone: 'info',
      icon: 'door-open',
      score: 70,
      label: 'Arrivals',
      text: (
        <>
          <strong>{pct(before, totalIn)}%</strong> of entries came before game start
          {lastHour > 0 && <>, <strong>{pct(lastHour, totalIn)}%</strong> in the last hour before it</>}.
          {after > 0 && <> <strong>{after.toLocaleString()}</strong> people ({pct(after, totalIn)}%) arrived after it started.</>}
        </>
      ),
    });
  }

  // 4. Exit wave: when most people left, and through which gate
  const exitWave = totalOut >= MIN_EXITS && totalOut >= totalIn * MIN_EXIT_SHARE ? peakStretch(allFlow, 'out') : null;
  if (exitWave) {
    const when = relativeToStart(exitWave, startInRange ? gameStart : null);
    const topExit = [...perGate].sort((a, b) => b.out - a.out)[0];
    insights.push({
      key: 'exits',
      tone: 'info',
      icon: 'box-arrow-right',
      score: isLive ? 60 : 85,
      label: 'Exit wave',
      text: (
        <>
          {isLive ? 'The most people have left' : 'Most people left'} <strong>{span(exitWave)}</strong>:
          {' '}<strong>{exitWave.total.toLocaleString()} exits</strong> ({pct(exitWave.total, totalOut)}% of all){when ? `, ${when}` : ''}.
          {topExit && topExit.out > 0 && perGate.length > 1 && (
            <> Most exits overall were through <strong>{topExit.label}</strong> ({pct(topExit.out, totalOut)}%).</>
          )}
        </>
      ),
    });
  }

  // 5. Queue pressure: the gate with the longest queues, and whether its traffic explains it
  const queued = perGate
    .filter((g) => g.queue.length)
    .map((g) => ({
      ...g,
      peak: peakQueue(g.queue), // rounded, first time reached: same as the gate card
      longMinutes: g.queue.filter((p) => isLongQueue(p.queueTime)).length,
    }))
    .sort((a, b) => b.longMinutes - a.longMinutes || b.peak.value - a.peak.value);
  const worst = queued[0];
  if (worst && worst.peak.value >= 1) {
    const share = pct(worst.in, totalIn);
    const isLong = worst.longMinutes > 0;
    // A gate that handled more people but kept a shorter queue: the queue isn't just about traffic
    const busierShorter = queued
      .filter((g) => g !== worst && g.in > worst.in && g.peak.value < worst.peak.value)
      .sort((a, b) => b.in - a.in)
      .map((g) => ({ label: g.label, in: g.in, peakValue: g.peak.value }))[0];
    insights.push({
      key: 'queues',
      tone: isLong ? 'bad' : 'good',
      icon: 'hourglass-split',
      score: isLong ? 95 : 40,
      label: isLong ? 'Longest queues' : 'Queues',
      text: isLong ? (
        <>
          <strong>{worst.label}</strong> had the longest queues: peak <strong>{worst.peak.value} min</strong> at
          {' '}{minuteToClock(worst.peak.minute)}, at {QUEUE_TARGET} min or more for <strong>{formatDuration(worst.longMinutes)}</strong>.
          {busierShorter && (
            <>
              {' '}<strong>{busierShorter.label}</strong> took more entries ({pct(busierShorter.in, totalIn)}% vs {share}%) with a
              {' '}shorter peak ({busierShorter.peakValue} min).
            </>
          )}
        </>
      ) : (
        <>
          Queues stayed short: every gate stayed under {QUEUE_TARGET} min. The longest was
          {' '}<strong>{worst.peak.value} min</strong> at <strong>{worst.label}</strong>.
        </>
      ),
    });
  }

  // 6. Uneven load between gates
  const byIn = perGate.filter((g) => g.in > 0).sort((a, b) => b.in - a.in);
  if (byIn.length >= 3) {
    const top = byIn[0];
    const low = byIn[byIn.length - 1];
    const ratio = top.in / low.in;
    if (ratio >= 1.8) {
      insights.push({
        key: 'balance',
        tone: 'info',
        icon: 'bar-chart-steps',
        score: 50,
        label: 'Uneven load',
        text: (
          <>
            <strong>{top.label}</strong> handled <strong>{ratio.toFixed(1)}×</strong> the entries of <strong>{low.label}</strong>
            {' '}({pct(top.in, totalIn)}% vs {pct(low.in, totalIn)}%).
          </>
        ),
      });
    }
  }

  return insights.sort((a, b) => b.score - a.score);
};
